import { useOrganizations, type Organization } from '@/hooks/useOrganizations';
import {
  OrganizationType,
  SubscriptionPlan,
  SubscriptionSource,
  UserTier,
} from '@/types/gql.generated';
import {
  getSubscriptionHealth,
  isSubscriptionInterrupted,
} from '../lib/subscriptionHealth';
import { useSubscription } from './useSubscription';

export type PersonalPlanState =
  /** No Plus access anywhere — the inline Plus/Family choices are offered. */
  | { kind: 'free' }
  /** Plus bought on the personal account through Stripe. */
  | { kind: 'plus' }
  /**
   * Plus bought on this account and set to cancel: still granting until
   * `endsAt`, but it won't renew. Distinct from `plus` because the date means
   * the opposite thing, and because there is something to undo.
   */
  | { kind: 'plus-ending'; endsAt: string | null }
  /**
   * A payment failed. Already defunded — the server grants on ACTIVE/TRIALING
   * only — but recoverable in the billing portal, and it must NOT be sold a
   * second subscription: the original still bills if it recovers.
   */
  | { kind: 'dunning'; plan: SubscriptionPlan; endedAt: string | null }
  /**
   * A subscription of their own that has terminally ended. Reads as Free for
   * every gate, but saying only "Free" hides the thing they'd want to act on.
   */
  | { kind: 'lapsed'; plan: SubscriptionPlan; endedAt: string | null }
  /**
   * Plus used to arrive through a workspace whose plan has since ended or
   * stopped paying. They can't fix that themselves — the point is to explain
   * where their Plus went and let them buy their own.
   */
  | {
      kind: 'inherited-lapsed';
      organization: Organization;
      workspace: 'family' | 'business';
    }
  /**
   * Plus granted offline: a MANUAL subscription, or a comped personal org with
   * no subscription behind it at all. Neither has a Stripe customer, so
   * self-service billing has to be swapped for a contact-support path.
   */
  | { kind: 'plus-manual' }
  /**
   * The user bought Family. Billing lives on that family's own panel, so the
   * personal panel only explains the relationship and links there.
   */
  | { kind: 'family-owner'; organization: Organization }
  /** Plus inherited from a workspace the user belongs to. */
  | {
      kind: 'inherited';
      organization: Organization;
      workspace: 'family' | 'business';
    };

const grantsPro = (org: Organization) => org.seatTier === UserTier.Pro;

/**
 * Resolves which personal-plan story to tell, so the panel's copy and CTAs
 * can't drift from the underlying entitlement.
 *
 * The two tiers on the personal org separate the cases: `seatTier` is what the
 * user pays for themselves (a Stripe or manual subscription, or a direct comp),
 * and `inheritedSeatTier` is what a workspace membership grants them. So
 * "I pay for Plus" and "a workspace gives me Plus" are read directly rather
 * than inferred.
 */
export const usePersonalPlanState = (): PersonalPlanState => {
  const { personalOrganization, sharedOrganizations } = useOrganizations();
  const { isManualSubscription } = useSubscription();

  if (personalOrganization && grantsPro(personalOrganization)) {
    // Family owner: the personal org is funded by a FAMILY subscription it
    // shares with a family org. Match on the shared subscriptionId so
    // additional families the user owns (their own separate subscriptions)
    // aren't mistaken for the source.
    if (
      personalOrganization.subscriptionId &&
      personalOrganization.subscriptionPlan === SubscriptionPlan.Family
    ) {
      const ownedFamily = sharedOrganizations.find(
        (org) =>
          org.type === OrganizationType.Family &&
          org.subscriptionId === personalOrganization.subscriptionId
      );
      if (ownedFamily) {
        return { kind: 'family-owner', organization: ownedFamily };
      }
    }

    const isOffline =
      isManualSubscription ||
      personalOrganization.subscriptionSource === SubscriptionSource.Manual ||
      // Comped: entitled with no subscription record to manage or renew.
      !personalOrganization.subscriptionId;

    if (isOffline) {
      return { kind: 'plus-manual' };
    }

    // Still granting, but set not to renew. Only visible now that the server
    // tracks cancel_at_period_end — Stripe keeps such a subscription `active`,
    // so before this the card said "Renews" to someone who had just canceled.
    const health = getSubscriptionHealth(personalOrganization);
    if (health.state === 'ending') {
      return { kind: 'plus-ending', endsAt: health.endsAt };
    }

    return { kind: 'plus' };
  }

  // Access is inherited from a membership. The personal org records *that* it
  // is inherited; the memberships say which workspace explains it. A user can
  // belong to a family *and* one or more businesses, and a family is the more
  // meaningful explanation, so look there first.
  if (personalOrganization?.inheritedSeatTier === UserTier.Pro) {
    const family = sharedOrganizations.find(
      (org) => org.type === OrganizationType.Family && grantsPro(org)
    );
    if (family) {
      return { kind: 'inherited', organization: family, workspace: 'family' };
    }

    const business = sharedOrganizations.find(
      (org) => org.type === OrganizationType.Business && grantsPro(org)
    );
    if (business) {
      return {
        kind: 'inherited',
        organization: business,
        workspace: 'business',
      };
    }
  }

  // Not entitled — but "not entitled" and "never paid" are different stories,
  // and collapsing them is what left a past-due owner being sold Plus while
  // their real problem was a card that needed updating. Their own subscription
  // is the more actionable explanation, so it's checked first.
  const ownHealth = getSubscriptionHealth(personalOrganization);
  if (isSubscriptionInterrupted(ownHealth)) {
    return {
      kind: ownHealth.state,
      plan: ownHealth.plan,
      endedAt: ownHealth.endedAt,
    };
  }

  // Their Plus came from a workspace whose plan has since stopped granting.
  // Matched on the workspace's own health rather than on `inheritedSeatTier`,
  // which the fan-out has already cleared by this point. Family before
  // business, for the same reason the granting branch above prefers it: someone
  // in both workspaces gets the more meaningful explanation, and which one that
  // is shouldn't depend on the order the API returned them in.
  const interrupted = (type: OrganizationType) =>
    sharedOrganizations.find(
      (org) =>
        org.type === type &&
        isSubscriptionInterrupted(getSubscriptionHealth(org))
    );

  const lapsedWorkspace =
    interrupted(OrganizationType.Family) ??
    interrupted(OrganizationType.Business);
  if (lapsedWorkspace) {
    return {
      kind: 'inherited-lapsed',
      organization: lapsedWorkspace,
      workspace:
        lapsedWorkspace.type === OrganizationType.Family
          ? 'family'
          : 'business',
    };
  }

  return { kind: 'free' };
};
